import { getNextCreditEpisode } from "../../../utils/videoCredits";
import { getNextSagaContent } from "../../../utils/sagaPlayback";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Helmet } from "react-helmet-async";
import api from '../../../services/api';
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useNav } from "../../../context/NavContext";
import { scrollToPageTop } from "../../../utils/scrollToPageTop";
import VideoPlayer from "../players/VideoPlayer";
import VideoDetails from "../components/VideoDetails";
import SerieDetails from "../components/SerieDetails";
import ContentCredits from "../components/ContentCredits";
import PlaybackContentTabs from "../components/PlaybackContentTabs";
import SeasonList from "../components/SeasonList";
import EpisodeList from "../components/EpisodeList";
import Notification from "../../../components/Notification";
import VideoList from "../components/VideoList";
import SeriesAndSeasonSelector from "../../../components/SeriesAndSeasonSelector"
import VideoExportDrawer from "../../../components/VideoExportDrawer";
import { buildCookieValue } from "../../../utils/cookieValue";
import PaginationPage from "../components/PaginationPage";
import ContentUniverseSection from "../components/ContentUniverseSection";
import SingleSagaSection from "../components/SingleSagaSection";
import ResumePlaybackDialog from "../components/ResumePlaybackDialog";
import { buildVideoPageMetadata } from "../../../utils/videoPageMetadata";


const apiUrl = process.env.REACT_APP_URL_LOCAL;
const LECTURE_TOP_ID = "lecture-top";
const LECTURE_TOP_ANCHOR = `#${LECTURE_TOP_ID}`;

const PREMIUM_MESSAGE = `Ce contenu est réservé aux membres Premium.
Il fait partie des vidéos et séries exclusives disponibles uniquement avec un abonnement actif.

Pour passer en Premium :
ouvrez Paramètres → Abonnement,
puis choisissez la formule qui vous convient (mensuelle ou annuelle).
L’activation est immédiate et ne nécessite aucun paiement réel : il s’agit d’une simulation pour tester les fonctionnalités.`;

const VideoSeePage = ({ PlayerComponent = VideoPlayer }) => {

  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const navigation = useNav();
  const setCinemaMode = navigation?.setCinemaMode;
  const cinemaActive = PlayerComponent === VideoPlayer && navigation?.cinemaMode;
  useEffect(() => () => setCinemaMode?.(false), [setCinemaMode]);
  const [creditData, setCreditData] = useState(null);
  const [creditError, setCreditError] = useState("");
  const [creditRevision, setCreditRevision] = useState(0);
  const refreshCredits = useCallback(() => setCreditRevision(n => n + 1), []);
  const [type, setType] = useState(null);
  const [video, setVideo] = useState(null);
  const [series, setSeries] = useState(null);
  const [seriesId, setSeriesId] = useState(null);
  const [currentSeason, setCurrentSeason] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [currentEpisode, setCurrentEpisode] = useState(null);
  const [notification, setNotification] = useState(null);
  const backgroundBlur = useRef(null);
  const [recommendations, setRecommendations] = useState([]);
  const [similarRecommendations, setSimilarRecommendations] = useState([]);
  const [discoveryRecommendations, setDiscoveryRecommendations] = useState([]);
  const [similarPanel, setSimilarPanel] = useState([]);
  const [discoveryPanel, setDiscoveryPanel] = useState([]);
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessMessage, setAccessMessage] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [resumeOpen, setResumeOpen] = useState(false);
  const [resumeTime, setResumeTime] = useState(null);
  const [videoElement, setVideoElement] = useState(null);
  const [isResettingSeries, setIsResettingSeries] = useState(false);
  const [skipFirstPlayLogKey, setSkipFirstPlayLogKey] = useState(0);
  const [multiAudioEnabled, setMultiAudioEnabled] = useState(false);
  const [resumeChoicePulse, setResumeChoicePulse] = useState(false);
  const [contentSagas, setContentSagas] = useState([]);
  const [singleSagaPlayback, setSingleSagaPlayback] = useState(null);
  const [contentUniverses, setContentUniverses] = useState([]);
  const [sagaPage, setSagaPage] = useState(1);
  const [sagaTotalPages, setSagaTotalPages] = useState(1);
  const [sagaTotalItems, setSagaTotalItems] = useState(0);
  const [selectedSaga, setSelectedSaga] = useState(null);
  const [selectedSagaDetails, setSelectedSagaDetails] = useState(null);
  const [selectedSagaLoading, setSelectedSagaLoading] = useState(false);
  const sagaRequestRef = useRef(0);
  const progressDeletedRef = useRef(false);
  const resumeProgressRef = useRef(null);
  const activeProgressLogActionRef = useRef("video_first_play");


  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await api.get('/users/me');
        //console.log('User profile data:', response.data); // Log des données utilisateur
        setUser(response.data);
      } catch (error) {
        console.error("Erreur lors de la récupération de l'utilisateur :", error);
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    let cancelled = false;

    api.get("/app-settings/multi-audio")
      .then((response) => {
        if (!cancelled) {
          setMultiAudioEnabled(Boolean(response.data?.active));
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setMultiAudioEnabled(false);
          console.warn(
            "Réglage multi-audio indisponible :",
            error.response?.data?.error || error.message
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);


  const handleConvertToEpisode = async () => {
    if (!video?.VideoID) return;

    try {
      const response = await api.put("/videos/move-to-season", {
        videoId: video.VideoID,
        SaisonID: selectedSeason || null,
      });

      showNotification({
        message: response.data?.message || "Vidéo déplacée avec succès.",
        icon: "✅",
        type: "success",

      });

      setTimeout(() => {
        window.location.reload();
      }, 100);
    } catch (error) {
      const msg =
        error.response?.data?.error ||
        error.message ||
        "Erreur lors du déplacement de la vidéo.";
      showNotification({ message: msg, icon: "❌", type: "error" });
    }
  };

  const handleResetSeriesWatchStatus = async () => {
    if (!seriesId || isResettingSeries) return;

    const confirmed = window.confirm(
      "Recommencer cette série ? Les épisodes repasseront en non vus, sans supprimer votre historique de lecture."
    );
    if (!confirmed) return;

    try {
      setIsResettingSeries(true);
      const response = await api.put(`/series/${seriesId}/watch-reset`);
      showNotification(
        response.data?.message || "La série a été remise à zéro.",
        "✅",
        "success"
      );
      await fetchVideo();
    } catch (error) {
      const msg =
        error.response?.data?.error ||
        error.message ||
        "Erreur lors de la remise à zéro de la série.";
      showNotification(msg, "❌", "error");
    } finally {
      setIsResettingSeries(false);
    }
  };

  useEffect(() => {
    // Appel à l'API pour obtenir les recommandations par genres (original)
    fetch(`${apiUrl}/api/videos/recommandation/${id}`, { credentials: "include" })
      .then(response => response.json())
      .then(data => setRecommendations(data))
      .catch(error => console.error('Error fetching recommendations:', error));
  }, [id]);

  useEffect(() => {
    const fetchPersonalizedRecommendations = async () => {
      try {
        const response = await api.get(`/videos/recommandation-personalisee/${id}`);
        setSimilarRecommendations(response.data?.similar || []);
        setDiscoveryRecommendations(response.data?.discovery || []);
      } catch (error) {
        console.error("Erreur lors de la récupération des recommandations personnalisées :", error);
        setSimilarRecommendations([]);
        setDiscoveryRecommendations([]);
      }
    };

    fetchPersonalizedRecommendations();
  }, [id]);

  const buildRotationPanels = (items, panelSize = 8, panelCount = 3) => {
    const source = Array.isArray(items) ? items.filter(Boolean) : [];
    const shuffled = [...source].sort(() => Math.random() - 0.5);
    const panels = [];
    const getKey = (item) => `${item?.type || "item"}:${item?.id ?? item?.FirstVideoID ?? ""}`;
    const pool = [...shuffled];

    for (let i = 0; i < panelCount; i += 1) {
      const chunk = pool.splice(0, panelSize);

      if (chunk.length < panelSize && shuffled.length > 0) {
        const used = new Set(chunk.map(getKey));
        for (const item of shuffled) {
          if (chunk.length >= panelSize) break;
          const key = getKey(item);
          if (used.has(key)) continue;
          used.add(key);
          chunk.push(item);
        }
      }

      panels.push(chunk);
    }

    return panels;
  };

  useEffect(() => {
    const panels = buildRotationPanels(similarRecommendations);
    const index = Math.floor(Math.random() * panels.length);
    setSimilarPanel(panels[index] || []);
  }, [similarRecommendations]);

  useEffect(() => {
    const panels = buildRotationPanels(discoveryRecommendations);
    const index = Math.floor(Math.random() * panels.length);
    setDiscoveryPanel(panels[index] || []);
  }, [discoveryRecommendations]);

  const fetchVideo = async () => {
    try {
      setIsLoading(true);
      setAccessMessage(null); // reset éventuel

      const response = await api.get(`/videos/${id}`);
      const data = response.data;

      setType(data.type);
      setVideo({
        ...data.video,
        subtitles:
          data.video.VideoSubtitles?.map((sub) => ({
            label: sub.Label,
            url: `${apiUrl}/${String(sub.CheminSubtitle).replace(/^\/+/, "")}`,
            language: sub.Language,
            type: sub.Type,
            origin: sub.Origin,
          })) || [],
        audioTracks:
          data.video.VideoAudioTracks?.map((track) => ({
            id: track.VideoAudioTrackID,
            label: track.Label,
            language: track.Language,
            playlist: track.CheminPlaylist,
            isDefault: Boolean(track.IsDefault),
            origin: track.Origin,
            synthetic: Boolean(track.Synthetic),
            order: track.Ordre,
          })) || [],
        aiFeaturesAccepted: data.aiFeaturesAccepted === true,
        mediaAccessToken: data.mediaAccessToken || null,
      });
      setCurrentEpisode(data.video);

      if (data.type === "series") {
        setSeries(data.series);
        setSeriesId(data.series.SeriesID);

        const matchingSeason = data.series.Saisons.find((saison) =>
          saison.Episodes.some(
            (episode) => episode.VideoID === parseInt(id, 10)
          )
        );

        setCurrentSeason(matchingSeason || null);
        setEpisodes(matchingSeason?.Episodes || []);
      } else {
        setSeries(null);
        setSeriesId(null);
        setCurrentSeason(null);
        setEpisodes([]);
      }
    } catch (error) {
      if (error.response) {
        const { status, data } = error.response;

        if (status === 401) {
          showNotification(
            "Session expirée ou non connectée. Merci de vous reconnecter.",
            "🔒",
            "error",
          );
          setAccessMessage("Vous devez être connecté pour accéder à cette vidéo.");
        } else if (status === 403 && data?.code === "PREMIUM_REQUIRED") {
          // contenu premium bloqué -> on affiche ton message
          showNotification(
            "Ce contenu est réservé aux membres premium.",
            "⭐",
            "warning",
          );
          setAccessMessage(PREMIUM_MESSAGE);
        } else {
          showNotification(
            data?.error || "Erreur lors du chargement de la vidéo.",
            "⚠️",
            "error",
          );
          setAccessMessage("Impossible de charger cette vidéo pour le moment.");
        }
      } else {
        showNotification(
          error.message || "Erreur lors du chargement de la vidéo.",
          "⚠️",
          "error",
        );
        setAccessMessage("Impossible de charger cette vidéo pour le moment.");
      }

      // On nettoie l'état vidéo
      setVideo(null);
      setSeries(null);
      setEpisodes([]);
    } finally {
      setIsLoading(false);
    }
  };

  const showNotification = (message, icon, type, duration = 7000) => {
    setNotification({ message, icon, type, duration });
  };

  useEffect(() => {
    fetchVideo();
  }, [id]); // Ajout de `id` comme dépendance pour recharger la page lorsque l'ID change

  useEffect(() => {
    setShowDetails(false); // on replie les détails quand on change de vidéo
  }, [id]);

  const handleSeasonChange = (season) => {
    setCurrentSeason(season);
    setEpisodes(season.Episodes);
  };

  const handleTitleUpdate = (newTitle) => {
    setVideo((prevVideo) => ({ ...prevVideo, Titre: newTitle }));
  };

  const handleResumerUpdate = (newResumer) => {
    setVideo((prevVideo) => ({ ...prevVideo, Resumer: newResumer }));
  };

  // ⬇️ nouveau: mise à jour de l'image après upload
  const handleImageUpdate = (newCheminImage) => {
    setVideo((prev) => ({ ...prev, CheminImage: newCheminImage }));
  };

  const handleSeriesTitleUpdate = (newTitle) => {
    setSeries((prevSeries) => ({ ...prevSeries, Titre: newTitle }));
  };

  const handleSeriesResumerUpdate = (newResumer) => {
    setSeries((prevSeries) => ({ ...prevSeries, Resumer: newResumer }));
  };

  // ⬇️ nouveau : mise à jour de l'image de la série après upload
  const handleSeriesImageUpdate = (newCheminImage) => {
    setSeries((prev) => ({ ...prev, CheminImage: newCheminImage }));
  };

  const isAdmin = user?.GradeID === 1 || user?.GradeID === 2;
  const isEpisode = Boolean(video?.SaisonID);
  const isSeries = type === "series" && Boolean(series);
  const isPremiumUser = isAdmin || !!(user?.PremiumEndDate && new Date(user.PremiumEndDate) > new Date());

  const NameApp = process.env.REACT_APP_NAME || "SAMI";
  const currentVideoId = video?.VideoID || null;
  const nextCreditEpisode = getNextCreditEpisode(series, currentVideoId, isPremiumUser);
  const hasCurrentSagaContents = singleSagaPlayback?.videoId === currentVideoId
    && singleSagaPlayback?.sagaId === contentSagas[0]?.SagaID;
  const nextSagaContent = getNextSagaContent({
    contents: hasCurrentSagaContents ? singleSagaPlayback.contents : [],
    sagaCount: sagaTotalItems,
    videoId: currentVideoId,
    series,
    premium: isPremiumUser,
  });
  const startNextFromZero = location.state?.creditNextVideoId === currentVideoId;
  const previewParam = new URLSearchParams(location.search).get("creditStart");
  const creditPreview = isAdmin && previewParam !== null && Number.isFinite(Number(previewParam)) && Number(previewParam) >= 0 ? Number(previewParam) : null;

  useEffect(() => {
    let cancelled = false;
    setCreditData(null); setCreditError("");
    if (!currentVideoId) return undefined;
    api.get(`/videos/${currentVideoId}/credits`).then(response => {
      if (!cancelled) setCreditData({ ...response.data, videoId: currentVideoId });
    }).catch(error => { if (!cancelled) setCreditError(error.response?.data?.error || "Chargement des génériques impossible."); });
    return () => { cancelled = true; };
  }, [currentVideoId, creditRevision]);

  useEffect(() => {
    window.addEventListener("focus", refreshCredits);
    return () => window.removeEventListener("focus", refreshCredits);
  }, [refreshCredits]);

  useEffect(() => {
    if (creditPreview !== null) setShowDetails(true);
  }, [creditPreview, currentVideoId]);

  const playNextCreditEpisode = () => {
    if (!nextCreditEpisode) return;
    videoElement?.pause();
    navigate(`/lecture/${nextCreditEpisode.VideoID}`, { state: { creditNextVideoId: nextCreditEpisode.VideoID } });
    scrollToLectureTop();
  };
  const playNextSagaContent = () => {
    if (!nextSagaContent) return;
    videoElement?.pause();
    navigate(`/lecture/${nextSagaContent.VideoID}`, { state: { creditNextVideoId: nextSagaContent.VideoID } });
    scrollToLectureTop();
  };
  const sagaItemsPerPage = 8;
  const pageOrigin = typeof window !== "undefined" ? window.location.origin : apiUrl;
  const metadataVideo = String(video?.VideoID || "") === String(id) ? video : null;
  const pageMetadata = buildVideoPageMetadata({
    id,
    video: metadataVideo,
    series: metadataVideo ? series : null,
    currentSeason: metadataVideo ? currentSeason : null,
    siteName: NameApp,
    pageOrigin,
    assetOrigin: apiUrl || pageOrigin,
  });

  const fetchContentSagas = useCallback(async (page = 1) => {
    if (!currentVideoId) return;
    const requestId = ++sagaRequestRef.current;

    try {
      const response = await api.get(`/sagas/content/video/${currentVideoId}`, {
        params: { page, limit: sagaItemsPerPage },
      });
      if (requestId !== sagaRequestRef.current) return;
      setContentSagas(response.data?.items || []);
      setSagaTotalItems(response.data?.totalItems || 0);
      setSagaTotalPages(response.data?.totalPages || 1);
      setSagaPage(page);
    } catch (error) {
      if (requestId !== sagaRequestRef.current) return;
      console.error("Erreur lors de la récupération des sagas du contenu :", error);
      setContentSagas([]);
      setSagaTotalItems(0);
      setSagaTotalPages(1);
      setSagaPage(1);
    }
  }, [currentVideoId]);

  useEffect(() => {
    setContentSagas([]);
    setSingleSagaPlayback(null);
    setSagaTotalItems(0);
    setSagaPage(1);
    setSagaTotalPages(1);
    fetchContentSagas(1);
    return () => { sagaRequestRef.current += 1; };
  }, [fetchContentSagas]);

  useEffect(() => {
    let cancelled = false;

    if (!currentVideoId) {
      setContentUniverses([]);
      return undefined;
    }

    setContentUniverses([]);
    api.get(`/universes/content/video/${currentVideoId}`)
      .then((response) => {
        if (!cancelled) {
          setContentUniverses(Array.isArray(response.data?.items) ? response.data.items : []);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Erreur lors de la récupération des univers du contenu :", error);
          setContentUniverses([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [currentVideoId]);

  const formatTimecode = (seconds) => {
    if (!Number.isFinite(seconds)) return "0:00";
    const totalSeconds = Math.max(0, Math.floor(seconds));
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    const paddedMins = hrs > 0 ? String(mins).padStart(2, "0") : String(mins);
    const paddedSecs = String(secs).padStart(2, "0");
    return hrs > 0 ? `${hrs}:${paddedMins}:${paddedSecs}` : `${paddedMins}:${paddedSecs}`;
  };

  useEffect(() => {
    // 1. Mettre à jour le cookie
    const expiresAt = new Date(Date.now() + 31536000 * 1000).toISOString();
    const cookieValue = buildCookieValue("dark", expiresAt);
    document.cookie = `theme=${cookieValue}; path=/; max-age=31536000`; // 1 an

    // 2. Forcer Tailwind à activer le theme dark
    document.documentElement.classList.add("dark");
  }, []);

  useEffect(() => {
    setResumeOpen(false);
    setResumeTime(null);
    setSkipFirstPlayLogKey(0);
    setResumeChoicePulse(false);
    resumeProgressRef.current = null;
    activeProgressLogActionRef.current = "video_first_play";
    progressDeletedRef.current = false;
    if (!currentVideoId || startNextFromZero || creditPreview !== null) return;

    let cancelled = false;

    const fetchProgress = async () => {
      try {
        const response = await api.get(`/videos/${currentVideoId}/progress`);
        if (cancelled) return;

        const progress = response.data?.progress || null;
        const savedTime = Number(progress?.Timecode);
        if (!Number.isFinite(savedTime) || savedTime <= 0) return;

        resumeProgressRef.current = progress;
        setResumeTime(savedTime);
        setResumeOpen(true);
      } catch (error) {
        console.warn("Récupération de la progression échouée:", error?.message || error);
      }
    };

    fetchProgress();

    return () => {
      cancelled = true;
    };
  }, [currentVideoId, startNextFromZero, creditPreview]);

  useEffect(() => {
    if (!videoElement || !currentVideoId) return;

    const saveProgress = async () => {
      if (!videoElement) return;
      if (progressDeletedRef.current) return;

      const currentTime = Math.floor(videoElement.currentTime);
      const duration = Math.floor(videoElement.duration);
      if (!Number.isFinite(currentTime) || currentTime <= 0) return;
      if (!Number.isFinite(duration) || duration <= 0) return;

      try {
        const response = await api.put(`/videos/${currentVideoId}/progress`, {
          Timecode: currentTime,
          Duration: duration,
          ProgressLogAction: activeProgressLogActionRef.current,
        });

        if (response.data?.deleted) {
          progressDeletedRef.current = true;
          setResumeOpen(false);
          setResumeTime(null);
        }
      } catch (error) {
        console.warn("Sauvegarde de la progression échouée:", error?.message || error);
      }
    };

    const intervalId = setInterval(saveProgress, 60000);
    videoElement.addEventListener("pause", saveProgress);
    videoElement.addEventListener("ended", saveProgress);

    return () => {
      clearInterval(intervalId);
      videoElement.removeEventListener("pause", saveProgress);
      videoElement.removeEventListener("ended", saveProgress);
    };
  }, [videoElement, currentVideoId]);

  const handleResume = () => {
    if (!videoElement || !Number.isFinite(resumeTime)) {
      setResumeOpen(false);
      return;
    }

    const seekTo = () => {
      try {
        videoElement.currentTime = resumeTime;
      } catch (error) {
        // ignore
      }
    };

    if (videoElement.readyState >= 1) {
      seekTo();
    } else {
      const onLoadedMetadata = () => {
        seekTo();
        videoElement.removeEventListener("loadedmetadata", onLoadedMetadata);
      };
      videoElement.addEventListener("loadedmetadata", onLoadedMetadata);
    }

    api.post("/logs/video-resume-play", {
      VideoID: currentVideoId,
      StartTimecode: resumeTime,
      Duration: resumeProgressRef.current?.Duration || Math.floor(videoElement.duration) || null,
    }).catch((error) => {
      console.warn("Log reprise vidéo échoué:", error?.message || error);
    });
    activeProgressLogActionRef.current = "video_resume_play";
    setSkipFirstPlayLogKey((prev) => prev + 1);

    setResumeOpen(false);
    setResumeChoicePulse(false);
  };

  const handleDismissResume = () => {
    if (currentVideoId) {
      api.delete(`/videos/${currentVideoId}/progress`, {
        data: { Source: "resume_modal" },
      }).catch((error) => {
        console.warn("Suppression de la progression échouée:", error?.message || error);
      });
    }
    setResumeOpen(false);
    setResumeChoicePulse(false);
    activeProgressLogActionRef.current = "video_first_play";
    resumeProgressRef.current = null;
  };

  const handleResumeModalOutsideClick = () => {
    setResumeChoicePulse(true);
    window.setTimeout(() => setResumeChoicePulse(false), 900);
  };

  const scrollToLectureTop = () => {
    scrollToPageTop();
  };

  const handleSagaContentClick = () => {
    setSelectedSaga(null);
    setSelectedSagaDetails(null);
    scrollToLectureTop();
  };

  const renderRecommendationSection = (title, videos, label = "Propositions") => (
    <section className="container mx-auto overflow-hidden rounded-2xl border border-sky-500/10 bg-white/80 shadow-xl shadow-slate-950/5 backdrop-blur dark:bg-slate-950/70 dark:shadow-sky-950/20">
      <div className="border-b border-sky-500/10 bg-gradient-to-r from-sky-500/15 via-blue-500/10 to-transparent px-6 py-5">
        <p className="text-sm font-bold uppercase text-sky-500 dark:text-sky-400">{label}</p>
        <h2 className="mt-1 text-2xl font-black text-slate-950 dark:text-white">
          {title}
        </h2>
      </div>
      <div className="relative p-6">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_12%_20%,rgba(14,165,233,0.10),transparent_26%),radial-gradient(circle_at_88%_0%,rgba(139,92,246,0.08),transparent_22%)]" />
        <div className="relative">
          <VideoList
            videos={videos}
            linkAnchor={LECTURE_TOP_ANCHOR}
            onContentClick={scrollToLectureTop}
          />
        </div>
      </div>
    </section>
  );

  const openSaga = async (saga) => {
    setSelectedSaga(saga);
    setSelectedSagaDetails(null);
    setSelectedSagaLoading(true);

    try {
      const response = await api.get(`/sagas/${saga.SagaID}`);
      setSelectedSagaDetails(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement de la saga :", error);
      showNotification("Impossible de charger cette saga.", "⚠️", "error");
      setSelectedSaga(null);
    } finally {
      setSelectedSagaLoading(false);
    }
  };

  const renderSagaSection = () => {
    if (!contentSagas.length) return null;
    if (sagaTotalItems === 1 && contentSagas.length === 1) {
      return <SingleSagaSection key={`${currentVideoId}:${contentSagas[0].SagaID}`}
        saga={contentSagas[0]} videoId={currentVideoId} seriesId={seriesId} embedded
        onContentsLoaded={setSingleSagaPlayback}
        linkAnchor={LECTURE_TOP_ANCHOR} onContentClick={scrollToLectureTop} />;
    }

    return (
      <div>
        <div className="container mx-auto grid grid-cols-2 gap-4 sm:grid-cols-4 xl:grid-cols-8">
          {contentSagas.map((saga) => (
            <button
              key={saga.SagaID}
              type="button"
              onClick={() => openSaga(saga)}
              className="group text-left transition duration-300 hover:-translate-y-2"
            >
              <div className="min-h-full h-max max-h-max">
                <div className="rounded-xl overflow-hidden border border-neutral-400 bg-gradient-to-br from-slate-950 to-slate-900 mb-2 relative transition duration-300 ease-in-out group-hover:border-blue-500">
                  <img
                    src={saga.CheminImage ? `${apiUrl}/${saga.CheminImage}` : "/imageDefault.png"}
                    alt={saga.Titre}
                    className="aspect-2/3 h-full w-full object-cover duration-300 group-hover:scale-110"
                  />
                  {saga.Premium && (
                    <span className="absolute left-2 top-2 z-10 inline-flex rounded-full border border-amber-200/40 bg-gradient-to-br from-amber-300/95 via-yellow-400/95 to-orange-400/95 px-2.5 py-1 text-[10px] font-black uppercase text-slate-950">
                      Premium
                    </span>
                  )}
                </div>
                <p className="px-2 py-1 text-center text-sm font-bold capitalize text-slate-900 line-clamp-2 dark:text-neutral-300">
                  {saga.Titre}
                </p>
              </div>
            </button>
          ))}
        </div>
        {sagaTotalItems > sagaItemsPerPage && (
          <PaginationPage
            currentPage={sagaPage}
            totalPages={sagaTotalPages}
            totalItems={sagaTotalItems}
            itemsPerPage={sagaItemsPerPage}
            onPageChange={fetchContentSagas}
          />
        )}
      </div>
    );
  };

  const renderVideoDetails = (embedded = false) => (
    <VideoDetails
      video={video}
      embedded={embedded}
      showCredits={!embedded}
      videoElement={videoElement}
      creditData={creditData?.videoId === currentVideoId ? creditData : null}
      creditError={creditError}
      onCreditsRefresh={refreshCredits}
      isAdmin={isAdmin}
      onTitleUpdate={handleTitleUpdate}
      onResumerUpdate={handleResumerUpdate}
      onImageUpdate={handleImageUpdate}
      onPremiumUpdate={(newPremium) =>
        setVideo((prev) => ({ ...prev, Premium: newPremium }))
      }
      isFavorite={!!video.IsFavorite}
      onFavoriteChange={(nextValue) =>
        setVideo((prev) => ({ ...prev, IsFavorite: nextValue }))
      }
    />
  );

  return (
    <div id={LECTURE_TOP_ID} className={`relative ${cinemaActive ? 'scroll-mt-0' : 'scroll-mt-16'}`}>
      <Helmet>
        <title>{pageMetadata.title}</title>
        <meta name="description" content={pageMetadata.description} />
        <meta name="application-name" content={pageMetadata.siteName} />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href={pageMetadata.canonicalUrl} />
        <meta property="og:type" content={pageMetadata.openGraphType} />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:site_name" content={pageMetadata.siteName} />
        <meta property="og:title" content={pageMetadata.title} />
        <meta property="og:description" content={pageMetadata.description} />
        <meta property="og:url" content={pageMetadata.canonicalUrl} />
        <meta property="og:image" content={pageMetadata.imageUrl} />
        <meta property="og:image:alt" content={pageMetadata.imageAlt} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageMetadata.title} />
        <meta name="twitter:description" content={pageMetadata.description} />
        <meta name="twitter:image" content={pageMetadata.imageUrl} />
        <meta name="twitter:image:alt" content={pageMetadata.imageAlt} />
      </Helmet>
      <canvas
        ref={backgroundBlur}
        aria-hidden="true"
        className="fixed inset-0 -z-10 h-full w-full blur-3xl opacity-70"
      />
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          icon={notification.icon}
          duration={notification.duration ?? 10000} // durée contrôlée ici
          onClose={() => setNotification(null)}     // on nettoie le state côté parent
        />
      )}
      {video && (
        <main className="flex flex-col grow gap-12">
          <section data-playback-stage data-cinema={cinemaActive ? "true" : undefined} className={`${cinemaActive ? 'h-dvh max-h-dvh p-24' : 'h-[calc(100vh-4rem)] max-h-[calc(100vh-4rem)] p-16'} box-border flex items-center`}>
            <div className="w-full h-full">
              <PlayerComponent
                video={video}
                onCinemaModeChange={setCinemaMode}
                backgroundBlur={backgroundBlur}
                onVideoElement={setVideoElement}
                skipFirstPlayLogKey={skipFirstPlayLogKey}
                multiAudioEnabled={multiAudioEnabled}
                onSubtitlesUpdated={fetchVideo}
                creditSegments={creditData?.videoId === currentVideoId ? creditData.items : []}
                nextEpisode={nextCreditEpisode}
                onNextEpisode={playNextCreditEpisode}
                nextSagaContent={nextSagaContent}
                onNextSagaContent={playNextSagaContent}
                initialPlaybackTime={creditPreview ?? (startNextFromZero ? 0 : null)}
                autoPlayOnLoad={startNextFromZero}
              />
            </div>
          </section>

          <div className="container px-4 mx-auto grid gap-12">
            {isEpisode && (
              <div className="flex flex-col gap-12">
                <button
                  onClick={() => setShowDetails((prev) => !prev)}
                  className="self-start rounded-lg border border-sky-300/40 bg-sky-500/15 px-5 py-2.5 text-sm font-bold text-slate-900 transition duration-200 hover:border-sky-300/80 hover:bg-sky-500/25 dark:text-white"
                >
                  {showDetails ? "Masquer les détails" : "Afficher les détails"}
                </button>
                {showDetails && renderVideoDetails()}
              </div>
            )}

            <PlaybackContentTabs
              key={`${currentVideoId}:${type}`}
              isSeries={isSeries}
              details={!isSeries && renderVideoDetails(true)}
              episodes={isSeries && (
                <>
                  <div className="mb-6 flex justify-end">
                    <button
                      type="button"
                      onClick={handleResetSeriesWatchStatus}
                      disabled={isResettingSeries}
                      className="inline-flex items-center justify-center rounded-lg border border-rose-300/40 bg-rose-500/15 px-5 py-2.5 text-sm font-bold text-rose-700 transition duration-200 hover:border-rose-300/80 hover:bg-rose-500/25 disabled:cursor-not-allowed disabled:opacity-60 dark:text-rose-200"
                    >
                      {isResettingSeries ? "Remise à zéro..." : "Recommencer la série"}
                    </button>
                  </div>
                  <SeasonList
                    seasons={series.Saisons}
                    currentSeason={currentSeason}
                    onSeasonChange={handleSeasonChange}
                  />
                  <EpisodeList
                    episodes={episodes}
                    currentEpisode={currentEpisode}
                    canAccessPremium={isPremiumUser}
                    linkAnchor={LECTURE_TOP_ANCHOR}
                    onEpisodeClick={scrollToLectureTop}
                  />
                </>
              )}
              series={isSeries && (
                <SerieDetails
                  series={series}
                  embedded
                  showCredits={false}
                  isAdmin={isAdmin}
                  onTitleUpdate={handleSeriesTitleUpdate}
                  onResumerUpdate={handleSeriesResumerUpdate}
                  onImageUpdate={handleSeriesImageUpdate}
                  onPremiumUpdate={(newPremium) =>
                    setSeries((prev) => ({ ...prev, Premium: newPremium }))
                  }
                  isFavorite={!!series.IsFavorite}
                  onFavoriteChange={(nextValue) =>
                    setSeries((prev) => ({ ...prev, IsFavorite: nextValue }))
                  }
                />
              )}
              credits={<ContentCredits content={isSeries ? series : video} isSeries={isSeries} embedded />}
              sagas={renderSagaSection()}
              universes={contentUniverses.length > 0 && <ContentUniverseSection universes={contentUniverses} embedded />}
            />

            {(user?.GradeID === 1 || user?.GradeID === 2) && (
              <section className="container mx-auto overflow-visible rounded-2xl border border-sky-500/10 bg-white/80 shadow-xl shadow-slate-950/5 backdrop-blur dark:bg-slate-950/70 dark:shadow-sky-950/20">
                <div className="rounded-t-2xl border-b border-sky-500/10 bg-gradient-to-r from-sky-500/15 via-blue-500/10 to-transparent px-6 py-5">
                  <p className="text-sm font-bold uppercase text-sky-500 dark:text-sky-400">Administration</p>
                  <h2 className="mt-1 text-2xl font-black text-slate-950 dark:text-white">Gestion de l'épisode</h2>
                </div>
                <div className="relative p-6">
                  <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_12%_20%,rgba(14,165,233,0.10),transparent_26%),radial-gradient(circle_at_88%_0%,rgba(139,92,246,0.08),transparent_22%)]" />
                  <div className="relative z-[80] grid grid-cols-1 md:grid-cols-2">
                    <div>
                      <SeriesAndSeasonSelector
                        selectedSeries={selectedSeries}
                        setSelectedSeries={setSelectedSeries}
                        selectedSeason={selectedSeason}
                        setSelectedSeason={setSelectedSeason}
                      />
                      <button
                        onClick={handleConvertToEpisode}
                        className="mt-4 inline-flex items-center justify-center rounded-lg border border-sky-300/40 bg-sky-500/15 px-5 py-2.5 text-sm font-bold text-slate-900 transition duration-200 hover:border-sky-300/80 hover:bg-sky-500/25 dark:text-white"
                      >
                        {selectedSeason ? "Convertir en épisode" : "Retirer de la série"}
                      </button>

                    </div>
                  </div>
                </div>
              </section>
            )}

            {user?.GradeID === 1 && (
              <section className="container mx-auto overflow-visible rounded-2xl border border-violet-400/20 bg-white/80 shadow-xl shadow-slate-950/5 backdrop-blur dark:bg-slate-950/70 dark:shadow-violet-950/20">
                <div className="rounded-t-2xl border-b border-violet-400/20 bg-gradient-to-r from-violet-500/20 via-sky-500/10 to-transparent px-6 py-5">
                  <p className="text-sm font-bold uppercase text-violet-600 dark:text-violet-300">
                    Super administration
                  </p>
                  <h2 className="mt-1 text-2xl font-black text-slate-950 dark:text-white">
                    Export vers le serveur principal
                  </h2>
                  <p className="mt-2 max-w-3xl text-sm font-semibold leading-6 text-slate-600 dark:text-slate-300">
                    Transfère cette vidéo et ses fichiers depuis le clone, adapte les références de la base principale et contrôle la réception.
                  </p>
                </div>
                <div className="relative p-6">
                  <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_12%_20%,rgba(139,92,246,0.10),transparent_26%),radial-gradient(circle_at_88%_0%,rgba(14,165,233,0.08),transparent_22%)]" />
                  <div className="relative">
                    <VideoExportDrawer
                      video={video}
                      seriesTitle={series?.Titre || null}
                      seasonNumber={currentSeason?.Numero ?? null}
                    />
                  </div>
                </div>
              </section>
            )}

            {/* recommandations personnalisées */}
            {renderRecommendationSection("Recommandations proches de vos goûts", similarPanel)}

            {renderRecommendationSection("Suggestions pour sortir de vos habitudes", discoveryPanel)}

            {/* recommandations par genres (original) */}
            {renderRecommendationSection("Recommandations proche du contenu que vous regardez", recommendations)}

          </div>

        </main>
      )}

      {video && (
        <ResumePlaybackDialog
          open={resumeOpen}
          onOutsideClick={handleResumeModalOutsideClick}
          onResume={handleResume}
          onRestart={handleDismissResume}
          resumeTimeLabel={formatTimecode(resumeTime)}
          choicePulse={resumeChoicePulse}
        />
      )}

      {selectedSaga && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 px-4 py-8 backdrop-blur">
          <div className="max-h-full w-full max-w-6xl overflow-y-auto rounded-2xl border border-sky-500/10 bg-white shadow-2xl dark:bg-slate-950 dark:text-white">
            <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-sky-500/10 bg-white/95 px-6 py-5 backdrop-blur dark:bg-slate-950/95">
              <div>
                <p className="text-sm font-bold uppercase text-sky-500 dark:text-sky-400">Saga</p>
                <h2 className="mt-1 text-2xl font-black text-slate-950 dark:text-white">{selectedSaga.Titre}</h2>
              </div>
              <button
                type="button"
                onClick={() => setSelectedSaga(null)}
                className="rounded-lg border border-slate-300/60 p-2 text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900"
              >
                <XMarkIcon className="size-5" />
              </button>
            </div>
            <div className="p-6">
              {selectedSagaLoading ? (
                <p className="text-sm font-semibold text-slate-500 dark:text-slate-300">Chargement...</p>
              ) : selectedSagaDetails?.Contents?.length ? (
                <VideoList
                  videos={selectedSagaDetails.Contents}
                  linkAnchor={LECTURE_TOP_ANCHOR}
                  onContentClick={handleSagaContentClick}
                />
              ) : (
                <p className="rounded-xl border border-sky-500/10 bg-slate-50 px-4 py-5 text-sm font-semibold text-slate-600 dark:bg-slate-900 dark:text-slate-300">
                  Aucun contenu dans cette saga.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Si pas de vidéo mais un message d'accès (premium ou autre) */}
      {!video && accessMessage && !isLoading && (
        <div className="max-w-2xl mx-auto mt-32 px-4 py-6 rounded-xl bg-slate-900/80 border border-slate-700 text-slate-100 shadow-lg">
          <h2 className="text-xl font-semibold mb-3">
            Accès au contenu restreint
          </h2>
          {PREMIUM_MESSAGE === accessMessage ? (
            <>
              <p className="mb-2">
                Ce contenu est réservé aux membres Premium. Il fait partie des vidéos et séries
                exclusives disponibles uniquement avec un abonnement actif.
              </p>
              <p className="mb-2">
                Pour passer en Premium, ouvrez <span className="font-semibold">Paramètres → Abonnement</span>,
                puis choisissez la formule qui vous convient (mensuelle ou annuelle).
              </p>
              <p className="text-sm text-slate-400">
                L’activation est immédiate et ne nécessite aucun paiement réel : il s’agit d’une
                simulation pour tester les fonctionnalités.
              </p>
            </>
          ) : (
            <p>{accessMessage}</p>
          )}
        </div>
      )}

      {/* État de chargement simple, uniquement si on charge encore */}
      {isLoading && !video && !accessMessage && (
        <div className="text-slate-400 font-medium text-center mt-32">
          Chargement en cours...
        </div>
      )}
    </div>
  );
};

export default VideoSeePage;
